import { Observable } from 'rxjs';

/**
 * Playback/live toolbar contract shared by every AIS sensor.
 *
 * A sensor's layer service (AISstream) or a dedicated controller (Voyage Radar)
 * implements this so the pluggable toolbar controls can drive playback without
 * knowing anything about the underlying operator, transport, or rendering.
 */

export type PlaybackMode = 'live' | 'playback' | 'idle';

/** One entry per ship-type category present in the loaded data (for filter chips). */
export interface PlaybackAvailableType {
  code: string;
  label: string;
  color: string;
  count: number;
}

/** Immutable snapshot of everything the toolbar controls need to render. */
export interface PlaybackState {
  mode: PlaybackMode;
  isPlaying: boolean;
  currentIndex: number;
  stepCount: number;
  /** ISO/UTC label per step — drives the timeline timestamp + slider ticks. */
  stepLabels: string[];
  /** Simulated-seconds per real-second (playback), or step interval. */
  speed: number;
  showLabels: boolean;
  /**
   * Group nearby vessels into count bubbles instead of drawing every hull.
   *
   * Optional, like the track fields below: `AisstreamPlaybackController` builds its
   * state as a bare literal, and it draws its own markers rather than going
   * through the Voyage Radar display pass, so it has no clustering to report.
   */
  clusterVessels?: boolean;
  /** Empty set = show all categories. */
  typeFilter: Set<string>;
  availableTypes: PlaybackAvailableType[];
  /** ISO timestamp of the latest data snapshot — used by the live 'last updated' control. */
  lastUpdated?: string | null;
  /**
   * True while a REFRESH is in flight — a paid pull of the latest positions.
   *
   * Deliberately not shared with the playback load below. The ON MAP bar renders
   * this as "REFRESHING · Pulling the latest vessel positions", which a replay of
   * already-saved snapshots is not: it costs nothing, changes nothing on the
   * operator's side, and can run for many seconds over a large archive.
   */
  loading?: boolean;

  // ── Archive replay ───────────────────────────────────────────────────────────
  /**
   * True while the saved snapshots for a replay are being fetched.
   *
   * The truth, and what every gate reads — a second entry while N frames are in
   * the air would start a competing load over the same window. GENERIC, not
   * Voyage Radar's: the playback-history entry control (a shared live-slot
   * control) gates itself on it, so any pull-operator replaying a saved archive
   * publishes it here.
   */
  playbackLoading?: boolean;

  // ── Selection ────────────────────────────────────────────────────────────────
  // GENERIC, and read by generic UI: the shared timeline control paints its
  // scrub rail in `activeVesselColor` on every operator's bars, and the
  // port-call/nearby drawers accent their headers from it. Any operator with a
  // selectable vessel publishes these three. Optional because AISstream has no
  // selection concept yet and builds its state without them.
  /** MMSI of the vessel currently selected on the map (null = none). */
  activeMmsi?: string | null;
  /** Display name of the active vessel (falls back to its MMSI). */
  activeVesselName?: string | null;
  /** The active vessel's own colour — drives the halo, trail and title dot. */
  activeVesselColor?: string | null;

  // ── Voyage Radar state — EXTRACTED (phase 0, REFACTOR-PLAN.md §5.4) ─────────
  // This interface used to carry three sections of VR-only optionals. They now
  // live on extending interfaces beside their one emitter each, so a new
  // operator's state is not a union of every other operator's fields:
  //   VoyageRadarPlaybackState (voyageradar-playback.controller.ts) — snapshot
  //     replay progress: playbackLoadingVisible · pendingPlayback ·
  //     playbackRefreshId · playbackSkipped
  //   VoyageTrackState (voyage-track.controller.ts) — track mode: trackSource ·
  //     activeTrackId · trackLoading(Visible) · pendingTrack · trackSpanMinutes ·
  //     creditsRemaining · trackCost · toiEnded · savedTracks · dimOthers ·
  //     othersOnMap
  //   VoyageVesselState (voyage-vessel.controller.ts) — located vessels:
  //     locatedVessels (and the LocatedVesselRow row type)
}

/**
 * The track a load in flight is about — everything the pending ON MAP row and
 * the spinning list row need to describe a wait truthfully.
 *
 * Built from the record the user clicked (a saved replay) or from the active
 * vessel plus the requested span (a paid fetch), so it is available on the same
 * frame as the click. Deliberately NOT a `SavedTrackRef`: a paid fetch has no
 * trackId yet and no stored point count, and this has to carry the vessel's
 * colour, which is a map fact rather than a stored one.
 */
export interface PendingTrackRef {
  /** Which stored track is being recalled; null for a paid fetch (no id yet). */
  trackId: string | null;
  /** 'saved' = free replay from storage, 'full' = paid operator fetch. */
  source: 'saved' | 'full';
  mmsi: string | null;
  /** Resolved display label — the same one the panel row shows. */
  label: string | null;
  /**
   * Whether that label is a real broadcast name or the "MMSI …" stand-in.
   *
   * Carried rather than re-derived: the label here is already RESOLVED, and
   * feeding it back through `resolveVesselIdentity` would read the stand-in as a
   * name and render "MMSI 636092371 · 636092371".
   */
  isNamed: boolean;
  /** The vessel's colour, so the pending rail matches the path about to appear. */
  color: string | null;
  /** Stored position count when known (saved replays only). */
  points: number | null;
  /** "24 h" — the span being recalled or bought. */
  spanLabel: string | null;
  /** "12 Jul 08:00 → 13 Jul 08:00", or null on a record with no window. */
  window: string | null;
}

/**
 * The replay a snapshot load in flight is about — what the pending ON MAP row
 * needs to describe the wait truthfully.
 *
 * The snapshot twin of `PendingTrackRef`, and deliberately much smaller: a
 * replay is about an archive rather than about a hull, so there is no identity,
 * colour or point count to carry — only how much of the record is coming and
 * which stretch of it. Both are known from `refreshHistory` on the same frame as
 * the click, so nothing here costs a request.
 */
export interface PendingPlaybackRef {
  /** Saved snapshots the load is about to fetch. */
  frames: number;
  /** "12 Jul 2026 – 28 Jul 2026", or null when the window cannot be described. */
  window: string | null;
}

/** A previously purchased vessel track that can be replayed at no cost. */
export interface SavedTrackRef {
  trackId: string;
  mmsi: string;
  vesselName?: string | null;
  from?: string;
  to?: string;
  count?: number;
  spanLabel?: string;
}

export const EMPTY_PLAYBACK_STATE: PlaybackState = {
  mode: 'idle',
  isPlaying: false,
  currentIndex: 0,
  stepCount: 0,
  stepLabels: [],
  speed: 60,
  showLabels: false,
  // On by default: the first thing a Voyage Radar pull puts on the map can be a
  // thousand hulls, and unclustered that is texture rather than a chart.
  clusterVessels: true,
  typeFilter: new Set<string>(),
  availableTypes: [],
  lastUpdated: null,
  loading: false,
  playbackLoading: false,
  activeMmsi: null,
  activeVesselName: null,
  activeVesselColor: null,
  // The Voyage Radar sections' defaults left with their fields (see the note on
  // PlaybackState above): each VR controller re-states its own beside its own
  // extending interface, so this constant carries only what every operator has.
};

/**
 * Contract every sensor's playback source implements so the toolbar controls
 * stay operator-agnostic. AISstream's `AisstreamPlaybackController` already has all of
 * the transport methods — it just additionally exposes `state$`.
 */
export abstract class PlaybackController {
  /** Reactive state the controls subscribe to. */
  abstract readonly state$: Observable<PlaybackState>;
  /** Synchronous current state (for non-reactive reads). */
  abstract snapshot(): PlaybackState;

  // ── Transport ──────────────────────────────────────────────────────────────
  abstract togglePlay(): void;
  abstract play(): void;
  abstract pause(): void;
  abstract seek(index: number): void;
  abstract stepForward(): void;
  abstract stepBack(): void;
  abstract skipToStart(): void;
  abstract skipToEnd(): void;
  abstract setSpeed(n: number): void;

  // ── Display ──────────────────────────────────────────────────────────────────
  abstract toggleLabels(): void;
  abstract setTypeFilter(set: Set<string>): void;

  /**
   * Group nearby vessels into count bubbles, or draw every hull. Optional: only
   * the sensors whose rendering goes through a clustering pass implement it.
   */
  toggleClusters(): void {
    /* optional override */
  }

  // ── Mode transitions ──────────────────────────────────────────────────────────
  /** Load history for a UTC range and enter playback. Resolves with step count. */
  abstract enterPlayback(fromIso: string, toIso: string): Promise<number>;
  /** Return to the live view (X on the playback toolbar). */
  abstract resumeLive(): void;
  /** Tear down / close the toolbar entirely. */
  abstract close(): void;

  // ── Pull-based live (optional) ────────────────────────────────────────────────
  /**
   * User-initiated refresh for pull-based operators (Voyage Radar). Streaming
   * operators (AISstream) leave this as a no-op.
   */
  refresh(): void {
    /* optional override */
  }

  /**
   * Open the operator-specific historical date-range picker and load playback.
   * The controller owns this because the selectable window (TOI) is runtime/
   * collection-specific. Default no-op.
   */
  requestHistory(): void {
    /* optional override */
  }

  /** Load ALL saved history and enter playback (optional; Voyage Radar). */
  enterHistoryPlayback(): Promise<number> {
    return Promise.resolve(0);
  }

  /**
   * Whether `enterHistoryPlayback` has anything to replay — the toolbar's entry
   * control withholds itself when this is false.
   *
   * A getter rather than a `PlaybackState` field because the archive is not the
   * controller's own state: it lives on the sensor document, which the host
   * mutates in place from a dozen call sites (every refresh path appends to
   * `refreshHistory`). Publishing it would mean a push from all of them, and one
   * missed push leaves a dead button on the bar. Read on each change-detection
   * pass instead, exactly as the sidebar's Playback pill reads `canPlayback`.
   *
   * Defaults to true: a sensor that offers the control at all is assumed able to
   * honour it unless it says otherwise.
   */
  get canPlayHistory(): boolean {
    return true;
  }

  /** Replace the available ship-type categories (optional; host computes from data). */
  setAvailableTypes(_types: PlaybackAvailableType[]): void {
    /* optional override */
  }

  // ── Voyage Radar track mode (optional) ───────────────────────────────────────
  /** Deselect the active vessel and tear down its track. Default no-op. */
  clearActiveVessel(): void {
    /* optional override */
  }

  /** Buy the dense operator track over `_minutes` (costs one historical fetch). */
  requestFullTrack(_minutes: number): void {
    /* optional override */
  }

  /** Replay an already-purchased track from storage (free). */
  requestSavedTrack(_trackId: string): void {
    /* optional override */
  }

  /** Fade / restore the vessels the loaded track is NOT about. Default no-op. */
  toggleDimOthers(): void {
    /* optional override */
  }
}
